import i18n from '@/plugins/vue-i18n'
import router from '@/router'
import { formatDateJP } from '@/utils/date'

/**
 * Format fullname
 * @param {*} lastName
 * @param {*} firstName
 * @returns
 */
export const fullName = (lastName, firstName) => `${lastName || ''} ${firstName || ''}`

/**
 * Format birth day
 * @param {*} year
 * @param {*} month
 * @param {*} day
 * @returns
 */
export const birthday = (year, month, day) => {
  if (!year || !month || !day) return ''

  return formatDateJP(new Date(`${year}/${month}/${day}`))
}

/**
 * Format address
 * @param {*} prefecture
 * @param {*} municipality
 * @param {*} address
 * @param {*} buildingApartmentName
 * @returns
 */
export const address = (prefecture, municipality, address, buildingApartmentName) =>
  `${prefecture || ''}${municipality || ''}${address + '　' || ''}${buildingApartmentName || ''}`

/**
 * Format postal code
 * @param {*} postalCodeFirst
 * @param {*} postalCodeLast
 * @returns
 */
export const postalCode = (postalCodeFirst, postalCodeLast) => `${postalCodeFirst}-${postalCodeLast}`

/**
 * Find key from value
 * @param {*} data
 * @param {*} value
 * @returns
 */
export const valueTokey = (data, value) => {
  const index = Object.values(data).findIndex((item) => item === value)
  return index > -1 ? Object.keys(data)[index] : ''
}

/**
 * Format currency
 * @param {*} num
 * @returns
 */
export const formatCurrency = (num) => i18n.n(num, 'currency', 'ja-JP')

/**
 * Redirect
 * @param {*} routerConfig
 * @returns
 */
export const redirect = (routerConfig) => router.push(routerConfig).catch((err) => err)

/**
 * Get card type from 4 digits first
 * @param {*} number
 * @returns
 */
export const getCardType = (number) => {
  // visa
  let re = new RegExp('^4')
  if (number.match(re) != null) return 'Visa'

  // Mastercard
  // Updated for Mastercard 2017 BINs expansion
  if (
    /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(number)
  )
    return 'Mastercard'

  // AMEX
  re = new RegExp('^3[47]')
  if (number.match(re) != null) return 'AMEX'

  // Discover
  re = new RegExp('^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)')
  if (number.match(re) != null) return 'Discover'

  // Diners
  re = new RegExp('^36')
  if (number.match(re) != null) return 'Diners'

  // Diners - Carte Blanche
  re = new RegExp('^30[0-5]')
  if (number.match(re) != null) return 'Diners - Carte Blanche'

  // JCB
  re = new RegExp('^35(2[89]|[3-8][0-9])')
  if (number.match(re) != null) return 'JCB'

  // Visa Electron
  re = new RegExp('^(4026|417500|4508|4844|491(3|7))')
  if (number.match(re) != null) return 'Visa Electron'

  return ''
}

/**
 * Parse address from string
 * @param {*} addressStr
 * @returns
 */
export const parseAddress = (addressStr) => {
  if (!addressStr) return ''

  const address = addressStr.match(/(\d+-?)+/g)
  const _address = address ? address[0] : null

  return {
    address: _address,
    municipality: addressStr.replace(new RegExp(_address), ''),
  }
}

/**
 * Only get number in string
 * @param {*} str
 * @returns
 */
export const getOnlyNumber = (str) => {
  if (!str) return ''

  return str.replace(/[^0-9.]/g, '')
}

/**
 * Format card number
 * @param {*} str
 * @returns
 */
export const formatCardNumber = (str) => {
  if (!str) return ''

  const num1 = str.slice(0, 4)
  const num4 = str.slice(-4)

  const remainSize = str.length - 8
  const surplus = remainSize % 2
  const lenNumCenter = Math.floor(remainSize / 2)
  const num2End = 4 + lenNumCenter + surplus
  const num2 = str.slice(4, num2End)
  const num3 = str.slice(num2End, num2End + lenNumCenter)

  return [num1, num2, num3, num4].join('-')
}

export const hiddenCardNumber = (num) =>
  Array(num?.length || 0)
    .fill('*')
    .join('')

/**
 * EC-3488
 * 1. PC - Required LINE
 * 6. SP - Required LINE
 * 8. PC - Optional LINE
 * 10 SP - Optional LINE
 * 12 PC - No LINE
 * 14 SP - No LINE
 */
export const DEVICE = {
  PC: 'pc',
  SP: 'sp',
}

export const getDevice = () =>
  /iphone|ipad|ipod|android/.test(window.navigator.userAgent.toLowerCase()) ? DEVICE.SP : DEVICE.PC

const LINE_OPTION = {
  NO_OPTION: 0,
  OPTIONAL: 1,
  REQUIRED: 2,
}

export const detectRequireLine = (shop) =>
  !shop.line_channel_flag
    ? LINE_OPTION.NO_OPTION
    : !shop.ec_line_required_flag
    ? LINE_OPTION.OPTIONAL
    : LINE_OPTION.REQUIRED

const DEVICE_TYPE = {
  PC_REQUIRED: 1,
  SP_REQUIRED: 6,
  PC_OPTIONAL: 8,
  SP_OPTIONAL: 10,
  PC_NO_OPTION: 12,
  SP_NO_OPTION: 14,
}
export const deviceType = (shop) => {
  const getDeviceTypeByDevice = (device = DEVICE.SP) =>
    device === DEVICE.SP
      ? [DEVICE_TYPE.SP_NO_OPTION, DEVICE_TYPE.SP_OPTIONAL, DEVICE_TYPE.SP_REQUIRED]
      : [DEVICE_TYPE.PC_NO_OPTION, DEVICE_TYPE.PC_OPTIONAL, DEVICE_TYPE.PC_REQUIRED]

  return getDeviceTypeByDevice(getDevice())[detectRequireLine(shop)]
}

export const formatCard = (number) => {
  if (!number) return ''
  const FORMAT_GMO_CARD = /(.{4})$/
  return '************$1'.replace('$1', number.split(FORMAT_GMO_CARD)[1])
}

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string' || phoneNumber.length <= 7) {
    return phoneNumber
  }
  return phoneNumber.replace(/^(\d{4})(\d{3})(\d*)$/, '$1-$2-$3')
}

export const subTotal = (price, quantity) => {
  if (!price || !quantity) return 0
  return Number(price) * Number(quantity)
}

export const subTotals = (array) => {
  if (!array || !array.length) return 0
  return array.reduce((acc, item) => acc + subTotal(item.price, item.quantity), 0)
}

export const subDiscounts = (array) => {
  if (!array || !array.length) return 0
  return array.reduce((acc, item) => acc + subTotal(item.discount, item.quantity), 0)
}
